import {
  SpaceDashboard,
  Apartment,
  Send,
  Person,
  QuestionMark,
  Logout,
  AssignmentOutlined,
  Home,
  Shield,
  BarChartOutlined,
  PeopleOutlined,
} from '@mui/icons-material';
import { externalLinks, externalTitles, routes } from '@/constants/Constants';
import { useLocation } from 'react-router';
import { useGoToExternal, useNavigateWithSegment } from '@/utils/linkUtils';
import { Box, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import useProfileDrawerStore from '@/stores/useProfileDrawerStore';
import { useUserQuery, UserPermissions, usePermission } from '@/shared/session';
import { logout } from '@/shared/api';
import { shouldDisableRenterInvite, shouldUserStayInEnrollProperty } from '@/utils/commonUtils';
import { useUrlChecker } from '@/routing/useUrlChecker';
import { useAnalyticsContext } from '@/shared/providers/analytics';
import {
  applicationTrackerNavigation,
  useInviteLeaseDetailsAction,
} from '@/modules/application-tracker';
import { SideMenuItem } from './MenuItem';
import { claimsNavigation, useClaimsNavigationAccess } from '@/modules/claims';
import { ZgriFeatureGuard, zgriNavigationPath } from '@/modules/zgri';

interface Props {
  menuOpen: boolean;
}

export function MenuContent({ menuOpen }: Props) {
  const navigate = useNavigateWithSegment();
  const goToExternal = useGoToExternal();
  const location = useLocation();
  const { showProfileDrawer } = useProfileDrawerStore();
  const { isPublic } = useUrlChecker();
  const { track } = useAnalyticsContext();

  const { enabled: canManageUsersEnabled } = usePermission(UserPermissions.User.CanManageUsers);

  const { notSet: canEnrollPropertiesNotSet, enabled: canEnrollPropertiesEnabled } = usePermission(
    UserPermissions.Properties.CanEnrollProperty,
  );
  const { notSet: userCanInviteRenterNotSet, enabled: userCanInviteRenterEnabled } = usePermission(
    UserPermissions.Invite.CanInviteRenter,
  );

  const canEnrollProperties = canEnrollPropertiesNotSet || canEnrollPropertiesEnabled;
  const userCanInviteRenter = userCanInviteRenterNotSet || userCanInviteRenterEnabled;
  const { canFileClaim: userCanFileClaim, canViewReport: userCanViewReportEnabled } =
    useClaimsNavigationAccess({ allowUnsetFileClaim: true });

  const { notSet: userCanAccessDealsDashboardNotSet, enabled: userCanAccessDealsDashboardEnabled } =
    usePermission(UserPermissions.Deal.CanAccessDealsDashboard);

  const userCanAccessDealsDashboard =
    userCanAccessDealsDashboardNotSet || userCanAccessDealsDashboardEnabled;

  const { openInviteLeaseDetails } = useInviteLeaseDetailsAction();

  const { data: user } = useUserQuery();

  const onLogOut = () => {
    track('Logout Button is clicked', {
      title: 'Logout',
      referrer: window.location.pathname,
    });
    logout({ reason: 'user_initiated' });
  };

  const onProfile = () => {
    track('Profile Button is clicked', {
      title: 'Profile',
      referrer: window.location.pathname,
    });
    showProfileDrawer();
  };

  const goToFAQ = () => {
    if (user && !canEnrollProperties) {
      return goToExternal(externalLinks.operatorsFAQ, {
        title: 'FAQ Menu (Operator)',
        path: externalLinks.operatorsFAQ,
      });
    }

    return goToExternal(externalLinks.faq, {
      title: 'FAQ Menu',
      path: 'enroll-property#faq',
    });
  };

  const shouldDisableActionIcons = shouldUserStayInEnrollProperty(location.pathname, user);
  const userIsOnHome = location.pathname === routes.home;
  const userIsOnPropertyEnrollment = location.pathname.includes(routes.propertyEnrollmentForm);
  const userIsOnDealsDashboard = location.pathname.includes(
    applicationTrackerNavigation.overviewPath,
  );
  const userIsOnClaimsReport = location.pathname === claimsNavigation.reportPath;
  const userIsOnClaimsForm = location.pathname.startsWith(claimsNavigation.submissionFormPath);

  return (
    <Grid
      container={true}
      sx={{
        height: '100%',
        pb: '8px',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        flexWrap: 'nowrap',
        justifyContent: 'space-between',
        flexDirection: 'column',
      }}
    >
      {!isPublic && (
        <Grid id="firstColumnIcons">
          <SideMenuItem
            open={menuOpen}
            text="Home"
            icon={<Home />}
            disabled={Boolean(shouldDisableActionIcons && !userIsOnHome)}
            selected={userIsOnHome}
            action={() => navigate(routes.home, { title: 'Visit Home Menu', path: '/' })}
          />
          {user && userCanInviteRenter ? (
            <SideMenuItem
              open={menuOpen}
              text="Invite Renter"
              icon={<Send />}
              action={() => openInviteLeaseDetails('side_menu')}
              disabled={shouldDisableActionIcons || shouldDisableRenterInvite(user)}
            />
          ) : null}
          {canEnrollProperties ? (
            <SideMenuItem
              open={menuOpen}
              text="Enroll Properties"
              icon={<Apartment />}
              disabled={Boolean(shouldDisableActionIcons && !userIsOnPropertyEnrollment)}
              selected={userIsOnPropertyEnrollment}
              action={() =>
                navigate(routes.propertyEnrollmentForm, {
                  title: 'Visit Enroll Properties Menu',
                  path: '/enroll/property-type',
                })
              }
            />
          ) : null}
          {user && userCanAccessDealsDashboard ? (
            <SideMenuItem
              open={menuOpen}
              text={'Application Tracker'}
              icon={<SpaceDashboard />}
              disabled={Boolean(shouldDisableActionIcons && !userIsOnDealsDashboard)}
              selected={userIsOnDealsDashboard}
              action={() => {
                navigate(applicationTrackerNavigation.overviewPath);
              }}
            />
          ) : null}
          <ZgriFeatureGuard>
            <SideMenuItem
              open={menuOpen}
              text="Renters Insurance"
              displayText={
                <Box component={'span'} display={'flex'} alignItems={'center'} columnGap={'8px'}>
                  <Typography component={'span'}>Renters Insurance</Typography>
                </Box>
              }
              icon={<Shield />}
              action={() =>
                navigate(zgriNavigationPath, {
                  title: 'Visit Renters Insurance',
                  path: zgriNavigationPath,
                })
              }
              selected={location.pathname.includes(zgriNavigationPath)}
            />
          </ZgriFeatureGuard>

          {userCanFileClaim ? (
            <SideMenuItem
              open={menuOpen}
              text={'File a Claim'}
              disabled={shouldDisableActionIcons || !userCanFileClaim}
              icon={<AssignmentOutlined />}
              action={() => navigate(claimsNavigation.submissionFormPath)}
              selected={userIsOnClaimsForm}
            />
          ) : null}

          {userCanViewReportEnabled ? (
            <SideMenuItem
              open={menuOpen}
              text="Track Claims"
              disabled={Boolean(shouldDisableActionIcons && !userIsOnClaimsReport)}
              icon={<BarChartOutlined />}
              action={() => navigate(claimsNavigation.reportPath)}
              selected={userIsOnClaimsReport}
            />
          ) : null}
        </Grid>
      )}
      <Grid>
        <Divider sx={{ m: '8px 0' }} />
        {canManageUsersEnabled ? (
          <SideMenuItem
            open={menuOpen}
            text="Users"
            icon={<PeopleOutlined />}
            action={() => navigate(routes.users)}
          />
        ) : null}
        {!isPublic && (
          <SideMenuItem open={menuOpen} text="Profile" icon={<Person />} action={onProfile} />
        )}
        <SideMenuItem
          open={menuOpen}
          text={externalTitles.faq}
          icon={<QuestionMark />}
          action={goToFAQ}
        />
        <SideMenuItem open={menuOpen} text="Logout" icon={<Logout />} action={onLogOut} />
      </Grid>
    </Grid>
  );
}
